import { useContext, useEffect, useRef, useState } from 'react';
import { Box, Icon, IconButton, Tooltip } from '@mui/material';

import AspectRatioBox from '../common/AspectRatioBox';

import { CarouselContext } from './CarouselContext';

// The bottom thumbnail bar for the carousel. Wraps each thumbnail a square aspect ratio box and a button that sets the active index to the index of the thumbnail.
export default function Thumbnails({ thumbnails }) {
    const { slides, currentIndex, handleSetIndex } = useContext(CarouselContext);
    const [visible, setVisible] = useState(true);

    const thumbnailRefs = useRef([]);

    useEffect(() => {
        if (thumbnailRefs.current[currentIndex]) {
            thumbnailRefs.current[currentIndex].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        }
    }, [currentIndex]);

    return (
        <Box
            sx={{
                height: 'auto',
                zIndex: 1,
                pointerEvents: 'auto'
            }}
        >
            {/* Show/Hide Thumbnails Button */}
            <Box sx={{ position: 'relative', zIndex: 2, display: 'flex', justifyContent: 'right', alignItems: 'right', height: 0, mr: '30px' }}>
                <Tooltip arrow title={visible ? 'Hide Thumbnails' : 'Show Thumbnails'} placement="top">
                    <IconButton onClick={() => setVisible(prevVisible => !prevVisible)} variant="showHide">
                        <Icon> {visible ? 'expand_more' : 'expand_less'}</Icon>
                    </IconButton>
                </Tooltip>
            </Box>

            <Box
                sx={{
                    height: visible ? '100%' : 0,
                    position: 'relative',
                    overflowY: 'hidden',
                    overflowX: 'scroll',
                    bottom: 0,
                    whiteSpace: 'nowrap',
                    borderTop: 'solid 2px white',
                    bgcolor: 'rgba(255,255,255,.6)',
                    scrollbarWidth: 'none',
                    '&::-webkit-scrollbar': {
                        display: 'none' // Hide the scrollbar for WebKit browsers (Chrome, Safari, Edge, etc.)
                    },
                    '&-ms-overflow-style:': {
                        display: 'none' // Hide the scrollbar for IE
                    }
                }}
            >
                {/* Thumbnail Sliding Track */}
                {slides.map((image, index) => (
                    <Box
                        key={index}
                        ref={el => (thumbnailRefs.current[index] = el)}
                        className={currentIndex === index ? 'active' : ''}
                        sx={{
                            width: ['25%', '25%', '20%', '14%', '12%'],
                            display: 'inline-block',
                            transition: 'all .5s ease-in-out',
                            m: [2, 2, 4],
                            mr: index === slides.length - 1 ? [2, 2, 4] : [0, 0, 0]
                        }}
                    >
                        <Box
                            component="button"
                            onClick={() => handleSetIndex(index)}
                            sx={{
                                borderColor: currentIndex === index ? 'success.main' : 'white',
                                borderStyle: 'solid',
                                borderWidth: 2,
                                borderRadius: 4,
                                width: 1,
                                overflow: 'hidden',
                                position: 'relative',
                                p: 0,
                                '&:hover': {
                                    borderColor: 'success.main'
                                }
                            }}
                        >
                            <AspectRatioBox>{thumbnails[index]}</AspectRatioBox>
                        </Box>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}
